import { type IntlShape } from 'react-intl'

import { useFormatPrice } from '@webapp/core/hooks/useFormatPrice'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type GlobalSearchResultTypeEnum,
  GlobalSearchResultTypes,
} from '../../../../../../value-objects/enums'
import { type GlobalSearchItemMetadata } from '../../../../../../value-objects/GlobalSearchItemMetadata'
import { useFormatGlobalSearchDateLabel } from '../../hooks/useFormatGlobalSearchDateLabel'

export const EXTERNAL_PAYMENT_METHOD = 'external'

function formatInvoiceNumber(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape
): Nullable<string> {
  if ('invoiceNumber' in metadata && metadata.invoiceNumber) {
    return intl.formatMessage(messages.invoiceNumberLabel, {
      invoiceNumber: metadata.invoiceNumber,
    })
  }
  return null
}

function formatPaymentMethod(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape
): Nullable<string> {
  if ('vendorPaymentMethod' in metadata && metadata.vendorPaymentMethod) {
    return metadata.vendorPaymentMethod === EXTERNAL_PAYMENT_METHOD
      ? intl.formatMessage(messages.externalPaymentMethodLabel)
      : metadata.vendorPaymentMethod
  }
  return null
}

function formatAmount(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape,
  formatPrice: ReturnType<typeof useFormatPrice>
): Nullable<string> {
  if ('amount' in metadata && metadata.amount) {
    return intl.formatMessage(messages.amountLabel, {
      amount: formatPrice(metadata.amount.value),
      currency: metadata.amount.currencyName,
    })
  }
  return null
}

function extractVendorName(
  metadata: GlobalSearchItemMetadata
): Nullable<string> {
  if ('vendorName' in metadata && metadata.vendorName != null) {
    return metadata.vendorName
  }
  return null
}

function extractSku(metadata: GlobalSearchItemMetadata): Nullable<string> {
  if ('sku' in metadata) {
    return metadata.sku
  }
  return null
}

function extractExternalId(
  metadata: GlobalSearchItemMetadata
): Nullable<string> {
  if ('externalId' in metadata) {
    return metadata.externalId
  }
  return null
}

function formatDate(
  type: GlobalSearchResultTypeEnum,
  metadata: GlobalSearchItemMetadata,
  formatDateLabel: ReturnType<typeof useFormatGlobalSearchDateLabel>
): Nullable<string> {
  if ('date' in metadata && metadata.date) {
    return formatDateLabel(type, metadata.date)
  }
  return null
}

function formatOwnerName(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape
): Nullable<string> {
  if ('ownerName' in metadata && metadata.ownerName != null) {
    return intl.formatMessage(messages.ownerNameLabel, {
      name: metadata.ownerName,
    })
  }
  return null
}

function formatEndDate(
  type: GlobalSearchResultTypeEnum,
  metadata: GlobalSearchItemMetadata,
  formatDateLabel: ReturnType<typeof useFormatGlobalSearchDateLabel>
): Nullable<string> {
  if ('endDate' in metadata && metadata.endDate) {
    return formatDateLabel(type, metadata.endDate)
  }
  return null
}

/**
 * Joins up to three non-null metadata parts into a single sub-text string.
 *
 * The three-part cap is a design constraint from Figma: the generic result row
 * template renders at most three metadata sub-text fields. Any parts beyond
 * the third are dropped intentionally.
 */
function joinParts(
  parts: Nullable<string>[],
  intl: IntlShape
): Nullable<string> {
  const filtered = parts.filter((part): part is string => part != null)

  if (filtered.length === 0) {
    return null
  }
  if (filtered.length === 1) {
    return filtered[0]
  }
  if (filtered.length === 2) {
    return intl.formatMessage(messages.twoPartLabel, {
      first: filtered[0],
      second: filtered[1],
    })
  }
  return intl.formatMessage(messages.threePartLabel, {
    first: filtered[0],
    second: filtered[1],
    third: filtered[2],
  })
}

export const useGlobalSearchListItemMetadata = () => {
  const intl = useProcurifyIntl()
  const formatDateLabel = useFormatGlobalSearchDateLabel()
  const formatPrice = useFormatPrice()

  return (
    type: GlobalSearchResultTypeEnum,
    metadata: GlobalSearchItemMetadata
  ): Nullable<string> => {
    if (type === GlobalSearchResultTypes.CONTRACT) {
      return joinParts(
        [
          extractVendorName(metadata),
          formatOwnerName(metadata, intl),
          formatEndDate(type, metadata, formatDateLabel),
        ],
        intl
      )
    }

    return joinParts(
      [
        extractVendorName(metadata),
        formatInvoiceNumber(metadata, intl),
        formatPaymentMethod(metadata, intl),
        extractSku(metadata),
        extractExternalId(metadata),
        formatDate(type, metadata, formatDateLabel),
        formatAmount(metadata, intl, formatPrice),
      ],
      intl
    )
  }
}
